import { CampaignWizardDraft } from "@interfaces";

/**
 * localStorage utility for persisting application state
 *
 * This module provides a centralized interface for managing browser localStorage
 * across the application. It handles JSON serialization/deserialization and error handling.
 *
 * Usage:
 * - Import specific storage modules: `import { themeStorage } from '@lib/localStorage'`
 * - Use type-safe methods for each feature area
 *
 * Available Storage Modules:
 * - themeStorage: Light/dark mode preference
 * - preferencesStorage: User preferences (active tab, etc.)
 * - notificationsStorage: Notification state
 */

const STORAGE_KEYS = {
  THEME: "bbb_theme",
  USER_PREFERENCES: "bbb_user_preferences",
  ACTIVE_TAB: "bbb_active_tab",
  NOTIFICATIONS: "bbb_notifications",
  USER_CAMPAIGN_PROGRESS: "bbb_user_campaign_progress", // Maps userId -> [communityIds]
  QUEST_FEEDBACK: "bbb_quest_feedback", // Maps questTitle -> { feedback: string, count: number }[]
  USER_FAVORITES: "bbb_user_favorites", // Maps userId -> [communityIds]
  EMAIL_VERIFICATION_STATUS: "bbb_email_verification_status", // Maps userId -> last sent timestamp
  COOKIE_CONSENT: "bbb_cookie_consent",
  CELEBRATION_MUTED: "bbb_celebration_sound_muted",
  ANIMATION_REDUCED: "bbb_animation_reduced",
  CAMPAIGN_COMPLETION_CELEBRATIONS: "bbb_campaign_completion_celebrations",
  ONBOARDING_COMPLETION_CELEBRATIONS: "bbb_onboarding_completion_celebrations",
  MEMBER_ONBOARDING_ACTIONS: "bbb_member_onboarding_actions",
  ACHIEVEMENT_CELEBRATIONS: "bbb_achievement_celebrations",
  PENDING_ACHIEVEMENT_CELEBRATION: "bbb_pending_achievement_celebration",
  COMMUNITY_CREATION_DRAFTS: "bbb_community_creation_drafts", // Maps userId -> draft community form data
  CAMPAIGN_WIZARD_DRAFTS: "bbb_campaign_wizard_drafts", // Maps userId -> projectName -> wizard state
} as const;

const getBrowserLocalStorage = (): Storage | null => {
  if (!globalThis.localStorage) {
    return null;
  }

  return globalThis.localStorage;
};

// Generic localStorage helpers
const storage = {
  get: <T>(key: string, defaultValue?: T): T | null => {
    const browserStorage = getBrowserLocalStorage();
    if (!browserStorage) {
      return defaultValue ?? null;
    }

    try {
      const item = browserStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue ?? null;
    } catch (error) {
      console.error(`Error reading from localStorage key "${key}":`, error);
      return defaultValue ?? null;
    }
  },

  set: <T>(key: string, value: T): void => {
    const browserStorage = getBrowserLocalStorage();
    if (!browserStorage) {
      return;
    }

    try {
      browserStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      const isQuotaExceeded =
        error instanceof DOMException &&
        (error.name === "QuotaExceededError" ||
          // Firefox
          error.name === "NS_ERROR_DOM_QUOTA_REACHED");

      if (isQuotaExceeded) {
        console.warn(
          `localStorage quota exceeded for key "${key}". Value was not persisted.`
        );
        return;
      }

      console.error(`Error writing to localStorage key "${key}":`, error);
    }
  },

  remove: (key: string): void => {
    const browserStorage = getBrowserLocalStorage();
    if (!browserStorage) {
      return;
    }

    try {
      browserStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  },

  clear: (): void => {
    const browserStorage = getBrowserLocalStorage();
    if (!browserStorage) {
      return;
    }

    try {
      browserStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },
};

// Local storage for ux improvement
export const communityDraftStorage = {
  // Limit stored logo size to avoid blowing past the ~5MB localStorage quota
  MAX_LOGO_BYTES: 150 * 1024,

  getCommunityCreationDraft: (
    userId: string
  ): { name: string; logo: string | null } | null => {
    const drafts =
      storage.get<Record<string, { name: string; logo: string | null }>>(
        STORAGE_KEYS.COMMUNITY_CREATION_DRAFTS,
        {}
      ) ?? {};
    return drafts[userId] ?? null;
  },

  setCommunityCreationDraft: (
    userId: string,
    data: { name: string; logo: string | null }
  ): void => {
    const trimmedLogo =
      data.logo && data.logo.length > communityDraftStorage.MAX_LOGO_BYTES
        ? null
        : data.logo;

    if (data.logo && trimmedLogo === null) {
      console.warn(
        `Community logo draft not persisted because it exceeds ${communityDraftStorage.MAX_LOGO_BYTES} bytes`
      );
    }

    const drafts =
      storage.get<Record<string, { name: string; logo: string | null }>>(
        STORAGE_KEYS.COMMUNITY_CREATION_DRAFTS,
        {}
      ) ?? {};
    drafts[userId] = { ...data, logo: trimmedLogo };
    storage.set(STORAGE_KEYS.COMMUNITY_CREATION_DRAFTS, drafts);
  },

  clearCommunityCreationDraft: (userId: string): void => {
    const drafts =
      storage.get<Record<string, { name: string; logo: string | null }>>(
        STORAGE_KEYS.COMMUNITY_CREATION_DRAFTS,
        {}
      ) ?? {};
    if (drafts[userId]) {
      delete drafts[userId];
      storage.set(STORAGE_KEYS.COMMUNITY_CREATION_DRAFTS, drafts);
    }
  },
};

export const campaignDraftStorage = {
  getDraft: (
    userId: string,
    projectName: string
  ): CampaignWizardDraft | null => {
    try {
      const drafts =
        storage.get<Record<string, Record<string, CampaignWizardDraft>>>(
          STORAGE_KEYS.CAMPAIGN_WIZARD_DRAFTS,
          {}
        ) ?? {};
      return drafts[userId]?.[projectName] ?? null;
    } catch (error) {
      console.error("Error reading campaign draft", error);
      return null;
    }
  },

  saveDraft: (
    userId: string,
    projectName: string,
    draft: CampaignWizardDraft
  ): void => {
    try {
      const drafts =
        storage.get<Record<string, Record<string, CampaignWizardDraft>>>(
          STORAGE_KEYS.CAMPAIGN_WIZARD_DRAFTS,
          {}
        ) ?? {};
      if (!drafts[userId]) {
        drafts[userId] = {};
      }
      drafts[userId][projectName] = draft;
      storage.set(STORAGE_KEYS.CAMPAIGN_WIZARD_DRAFTS, drafts);
    } catch (error) {
      console.error("Error saving campaign draft", error);
    }
  },

  clearDraft: (userId: string, projectName: string): void => {
    try {
      const drafts =
        storage.get<Record<string, Record<string, CampaignWizardDraft>>>(
          STORAGE_KEYS.CAMPAIGN_WIZARD_DRAFTS,
          {}
        ) ?? {};
      if (drafts[userId]?.[projectName]) {
        delete drafts[userId][projectName];
        storage.set(STORAGE_KEYS.CAMPAIGN_WIZARD_DRAFTS, drafts);
      }
    } catch (error) {
      console.error("Error clearing campaign draft", error);
    }
  },
};

export const themeStorage = {
  getTheme: (): "light" | "dark" | null => {
    return storage.get(STORAGE_KEYS.THEME);
  },

  setTheme: (theme: "light" | "dark"): void => {
    storage.set(STORAGE_KEYS.THEME, theme);
  },
};

const preferencesStorage = {
  getPreferences: (): Record<string, any> => {
    return storage.get(STORAGE_KEYS.USER_PREFERENCES, {}) ?? {};
  },

  setPreferences: (preferences: Record<string, any>): void => {
    storage.set(STORAGE_KEYS.USER_PREFERENCES, preferences);
  },

  updatePreference: (key: string, value: any): void => {
    const current = preferencesStorage.getPreferences();
    storage.set(STORAGE_KEYS.USER_PREFERENCES, { ...current, [key]: value });
  },

  getActiveTab: (): string | null => {
    return storage.get(STORAGE_KEYS.ACTIVE_TAB);
  },

  setActiveTab: (tab: string): void => {
    storage.set(STORAGE_KEYS.ACTIVE_TAB, tab);
  },
};

export const cookieConsentStorage = {
  getConsent: (): boolean => {
    return storage.get(STORAGE_KEYS.COOKIE_CONSENT, false) ?? false;
  },

  setConsent: (accepted: boolean): void => {
    storage.set(STORAGE_KEYS.COOKIE_CONSENT, accepted);
  },
};

export const celebrationSettingsStorage = {
  isMuted: (): boolean => {
    return storage.get(STORAGE_KEYS.CELEBRATION_MUTED, false) ?? false;
  },

  setMuted: (muted: boolean): void => {
    storage.set(STORAGE_KEYS.CELEBRATION_MUTED, muted);
  },
};

export const animationSettingsStorage = {
  isReduced: (): boolean => {
    return storage.get(STORAGE_KEYS.ANIMATION_REDUCED, false) ?? false;
  },

  setReduced: (reduced: boolean): void => {
    storage.set(STORAGE_KEYS.ANIMATION_REDUCED, reduced);
  },
};

export const campaignCelebrationStorage = {
  hasCampaignCompletionCelebrated: (userId: string, campaignId: string): boolean => {
    const map =
      storage.get<Record<string, true>>(
        STORAGE_KEYS.CAMPAIGN_COMPLETION_CELEBRATIONS,
        {},
      ) ?? {};
    return Boolean(map[`${userId}:${campaignId}`]);
  },

  markCampaignCompletionCelebrated: (userId: string, campaignId: string): void => {
    const map =
      storage.get<Record<string, true>>(
        STORAGE_KEYS.CAMPAIGN_COMPLETION_CELEBRATIONS,
        {},
      ) ?? {};
    map[`${userId}:${campaignId}`] = true;
    storage.set(STORAGE_KEYS.CAMPAIGN_COMPLETION_CELEBRATIONS, map);
  },
};

export const onboardingCelebrationStorage = {
  hasCompletedCelebrated: (userId: string): boolean => {
    const map =
      storage.get<Record<string, true>>(
        STORAGE_KEYS.ONBOARDING_COMPLETION_CELEBRATIONS,
        {},
      ) ?? {};
    return Boolean(map[userId]);
  },

  markCompletedCelebrated: (userId: string): void => {
    const map =
      storage.get<Record<string, true>>(
        STORAGE_KEYS.ONBOARDING_COMPLETION_CELEBRATIONS,
        {},
      ) ?? {};
    map[userId] = true;
    storage.set(STORAGE_KEYS.ONBOARDING_COMPLETION_CELEBRATIONS, map);
  },
};

export type MemberOnboardingActionStep =
  | "find-leaderboard"
  | "high-five"
  | "refer-friend"
  | "view-achievements";

const MEMBER_ONBOARDING_ACTION_EVENT = "bbb:member-onboarding-action";

export type MemberOnboardingActionEventDetail = {
  userId: string;
  step: MemberOnboardingActionStep;
  completedSteps: Record<MemberOnboardingActionStep, boolean>;
};

function getDefaultMemberOnboardingActions(): Record<
  MemberOnboardingActionStep,
  boolean
> {
  return {
    "find-leaderboard": false,
    "high-five": false,
    "refer-friend": false,
    "view-achievements": false,
  };
}

function dispatchMemberOnboardingActionEvent(
  detail: MemberOnboardingActionEventDetail,
) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<MemberOnboardingActionEventDetail>(
      MEMBER_ONBOARDING_ACTION_EVENT,
      { detail },
    ),
  );
}

export const memberOnboardingActionStorage = {
  eventName: MEMBER_ONBOARDING_ACTION_EVENT,

  getCompletedSteps: (userId: string): Record<MemberOnboardingActionStep, boolean> => {
    const map =
      storage.get<Record<string, Partial<Record<MemberOnboardingActionStep, boolean>>>>(
        STORAGE_KEYS.MEMBER_ONBOARDING_ACTIONS,
        {},
      ) ?? {};
    const userSteps = map[userId] ?? {};

    return {
      ...getDefaultMemberOnboardingActions(),
      "find-leaderboard": Boolean(userSteps["find-leaderboard"]),
      "high-five": Boolean(userSteps["high-five"]),
      "refer-friend": Boolean(userSteps["refer-friend"]),
      "view-achievements": Boolean(userSteps["view-achievements"]),
    };
  },

  markStepCompleted: (
    userId: string,
    step: MemberOnboardingActionStep,
  ): Record<MemberOnboardingActionStep, boolean> => {
    const map =
      storage.get<Record<string, Partial<Record<MemberOnboardingActionStep, boolean>>>>(
        STORAGE_KEYS.MEMBER_ONBOARDING_ACTIONS,
        {},
      ) ?? {};
    map[userId] = {
      ...map[userId],
      [step]: true,
    };
    storage.set(STORAGE_KEYS.MEMBER_ONBOARDING_ACTIONS, map);

    const completedSteps =
      memberOnboardingActionStorage.getCompletedSteps(userId);
    dispatchMemberOnboardingActionEvent({ userId, step, completedSteps });

    return completedSteps;
  },
};

export const achievementCelebrationStorage = {
  markCelebrated: (userId: string, key: string): void => {
    const map =
      storage.get<Record<string, true>>(STORAGE_KEYS.ACHIEVEMENT_CELEBRATIONS, {}) ??
      {};
    map[`${userId}:${key}`] = true;
    storage.set(STORAGE_KEYS.ACHIEVEMENT_CELEBRATIONS, map);
  },

  hasCelebrated: (userId: string, key: string): boolean => {
    const map =
      storage.get<Record<string, true>>(STORAGE_KEYS.ACHIEVEMENT_CELEBRATIONS, {}) ??
      {};
    return Boolean(map[`${userId}:${key}`]);
  },

  setPending: (payload: {
    userId: string;
    sourceId: string;
    title?: string;
    message?: string;
  }): void => {
    storage.set(STORAGE_KEYS.PENDING_ACHIEVEMENT_CELEBRATION, payload);
  },

  consumePending: (
    userId: string
  ): { userId: string; sourceId: string; title?: string; message?: string } | null => {
    const pending = storage.get<{
      userId: string;
      sourceId: string;
      title?: string;
      message?: string;
    }>(STORAGE_KEYS.PENDING_ACHIEVEMENT_CELEBRATION, null);

    if (!pending || pending.userId !== userId) return null;
    storage.remove(STORAGE_KEYS.PENDING_ACHIEVEMENT_CELEBRATION);
    return pending;
  },
};

//TODO fetch from API
//could be improved to fetch only the unread ones and keep the read ones in local storage or indexed db.
export const notificationsStorage = {
  getNotifications: (): any[] => {
    return storage.get(STORAGE_KEYS.NOTIFICATIONS, []) ?? [];
  },

  setNotifications: (notifications: any[]): void => {
    storage.set(STORAGE_KEYS.NOTIFICATIONS, notifications);
  },
};

export const emailVerificationStorage = {
  getLastSentAt: (userId: string): number | null => {
    const sentMap =
      storage.get<Record<string, number>>(
        STORAGE_KEYS.EMAIL_VERIFICATION_STATUS,
        {}
      ) ?? {};
    const timestamp = sentMap[userId];
    return typeof timestamp === "number" ? timestamp : null;
  },

  setLastSentAt: (userId: string, timestamp: number): void => {
    const sentMap =
      storage.get<Record<string, number>>(
        STORAGE_KEYS.EMAIL_VERIFICATION_STATUS,
        {}
      ) ?? {};
    sentMap[userId] = timestamp;
    storage.set(STORAGE_KEYS.EMAIL_VERIFICATION_STATUS, sentMap);
  },

  clearForUser: (userId: string): void => {
    const sentMap =
      storage.get<Record<string, number>>(
        STORAGE_KEYS.EMAIL_VERIFICATION_STATUS,
        {}
      ) ?? {};
    if (sentMap[userId]) {
      delete sentMap[userId];
      storage.set(STORAGE_KEYS.EMAIL_VERIFICATION_STATUS, sentMap);
    }
  },
};

//TODO use api
// Campaign progress tracking
const CAMPAIGN_PROGRESS_KEY = "bbb_campaign_progress";

export const campaignProgressStorage = {
  // Legacy methods - Get all campaigns a user has started
  getUserCampaigns: (userId: string): string[] => {
    const progressMap =
      storage.get<Record<string, string[]>>(
        STORAGE_KEYS.USER_CAMPAIGN_PROGRESS,
        {}
      ) ?? {};
    return progressMap[userId] || [];
  },

  // Mark a campaign as started for a user
  startCampaign: (userId: string, communityId: string): void => {
    const progressMap =
      storage.get<Record<string, string[]>>(
        STORAGE_KEYS.USER_CAMPAIGN_PROGRESS,
        {}
      ) ?? {};

    const userCampaigns = progressMap[userId] || [];

    // Only add if not already in progress
    if (!userCampaigns.includes(communityId)) {
      userCampaigns.push(communityId);
      progressMap[userId] = userCampaigns;
      storage.set(STORAGE_KEYS.USER_CAMPAIGN_PROGRESS, progressMap);
      console.log(`✅ Campaign ${communityId} started for user ${userId}`);
    }
  },

  // Remove a campaign from user's in-progress list (e.g., when completed)
  removeCampaign: (userId: string, communityId: string): void => {
    const progressMap =
      storage.get<Record<string, string[]>>(
        STORAGE_KEYS.USER_CAMPAIGN_PROGRESS,
        {}
      ) ?? {};

    const userCampaigns = progressMap[userId] || [];
    progressMap[userId] = userCampaigns.filter((id) => id !== communityId);
    storage.set(STORAGE_KEYS.USER_CAMPAIGN_PROGRESS, progressMap);
    console.log(
      `✅ Campaign ${communityId} removed from progress for user ${userId}`
    );
  },

  // Get count of in-progress campaigns (with at least 1 completed quest)
  getInProgressCount: (userId: string): number => {
    const allProgress =
      storage.get<
        Record<
          string,
          Record<
            string,
            Record<
              string,
              Record<
                string,
                { completed: boolean; passed?: boolean; completedAt?: string }
              >
            >
          >
        >
      >(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    const userProgress = allProgress[userId];
    if (!userProgress) return 0;

    let count = 0;

    // Count projects where user has completed at least 1 quest
    for (const projectName in userProgress) {
      const projectProgress = userProgress[projectName];
      let hasCompletedQuest = false;

      // Check all campaigns in this community
      for (const campaignId in projectProgress) {
        const campaignQuests = projectProgress[campaignId];

        // Check if any quest is completed AND passed
        for (const questId in campaignQuests) {
          const quest = campaignQuests[questId];
          if (quest.completed && quest.passed) {
            hasCompletedQuest = true;
            break;
          }
        }

        if (hasCompletedQuest) break;
      }

      if (hasCompletedQuest) count++;
    }

    return count;
  },

  // Check if a user has started a specific campaign
  hasCampaignStarted: (userId: string, communityId: string): boolean => {
    const userCampaigns = campaignProgressStorage.getUserCampaigns(userId);
    return userCampaigns.includes(communityId);
  },

  // Detailed quest progress methods
  // Get user's progress for a specific campaign
  getCampaignProgress: (
    userId: string,
    projectName: string,
    campaignId: string
  ): Record<
    string,
    { completed: boolean; passed?: boolean; completedAt?: string }
  > => {
    const allProgress =
      storage.get<
        Record<
          string,
          Record<
            string,
            Record<
              string,
              Record<
                string,
                { completed: boolean; passed?: boolean; completedAt?: string }
              >
            >
          >
        >
      >(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    return allProgress[userId]?.[projectName]?.[campaignId] || {};
  },

  // Update quest completion status
  updateQuestProgress: (
    userId: string,
    projectName: string,
    campaignId: string,
    questId: string,
    data: { completed: boolean; passed?: boolean; completedAt?: string }
  ): void => {
    const allProgress =
      storage.get<
        Record<
          string,
          Record<
            string,
            Record<
              string,
              Record<
                string,
                { completed: boolean; passed?: boolean; completedAt?: string }
              >
            >
          >
        >
      >(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    if (!allProgress[userId]) allProgress[userId] = {};
    if (!allProgress[userId][projectName])
      allProgress[userId][projectName] = {};
    if (!allProgress[userId][projectName][campaignId])
      allProgress[userId][projectName][campaignId] = {};

    allProgress[userId][projectName][campaignId][questId] = data;

    storage.set(CAMPAIGN_PROGRESS_KEY, allProgress);
    console.log(
      `✅ Updated progress for quest ${questId} in campaign ${campaignId}`
    );
  },

  // Submit a quest (mark as pending/under review)
  submitQuest: (
    userId: string,
    projectName: string,
    campaignId: string,
    questId: string,
    questTitle: string,
    chapterName?: string
  ): void => {
    const allProgress = storage.get<any>(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    if (!allProgress[userId]) allProgress[userId] = {};
    if (!allProgress[userId][projectName])
      allProgress[userId][projectName] = {};
    if (!allProgress[userId][projectName][campaignId])
      allProgress[userId][projectName][campaignId] = {};

    // Save quest as submitted (pending approval)
    allProgress[userId][projectName][campaignId][questId] = {
      submitted: true,
      approved: false,
      title: questTitle,
      chapter: chapterName || "General",
      submittedAt: new Date().toISOString(),
    };

    storage.set(CAMPAIGN_PROGRESS_KEY, allProgress);
    console.log(
      `✅ Quest submitted for review: ${questId} in campaign ${campaignId}`
    );

    // Dispatch event to trigger UI refresh
    window.dispatchEvent(new Event("questSubmitted"));
  },

  // Get all campaigns a user has participated in for a community
  getUserProjectCampaigns: (userId: string, projectName: string): string[] => {
    const allProgress =
      storage.get<
        Record<
          string,
          Record<
            string,
            Record<
              string,
              Record<
                string,
                { completed: boolean; passed?: boolean; completedAt?: string }
              >
            >
          >
        >
      >(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    return Object.keys(allProgress[userId]?.[projectName] || {});
  },

  // Clear all progress for a user in a specific campaign
  clearCampaignProgress: (
    userId: string,
    projectName: string,
    campaignId: string
  ): void => {
    const allProgress =
      storage.get<
        Record<
          string,
          Record<
            string,
            Record<
              string,
              Record<
                string,
                { completed: boolean; passed?: boolean; completedAt?: string }
              >
            >
          >
        >
      >(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    if (allProgress[userId]?.[projectName]?.[campaignId]) {
      delete allProgress[userId][projectName][campaignId];
      storage.set(CAMPAIGN_PROGRESS_KEY, allProgress);
      console.log(`🗑️ Cleared progress for campaign ${campaignId}`);
    }
  },

  // Check if user has completed at least 1 quest (and earned 1 block) in a community
  hasCompletedQuestInProject: (
    userId: string,
    projectName: string
  ): boolean => {
    const allProgress =
      storage.get<
        Record<
          string,
          Record<
            string,
            Record<
              string,
              Record<
                string,
                { completed: boolean; passed?: boolean; completedAt?: string }
              >
            >
          >
        >
      >(CAMPAIGN_PROGRESS_KEY, {}) ?? {};

    const projectProgress = allProgress[userId]?.[projectName];
    if (!projectProgress) return false;

    // Check all campaigns in this community
    for (const campaignId in projectProgress) {
      const campaignQuests = projectProgress[campaignId];

      // Check if any quest is completed AND passed (meaning user earned the block)
      for (const questId in campaignQuests) {
        const quest = campaignQuests[questId];
        if (quest.completed && quest.passed) {
          return true; // Found at least 1 completed & passed quest
        }
      }
    }

    return false; // No completed quests found
  },
};

export const questFeedbackStorage = {
  // Track feedback usage for a specific quest
  trackFeedback: (questTitle: string, feedback: string): void => {
    if (!feedback || !feedback.trim()) return;

    const feedbackMap =
      storage.get<Record<string, Array<{ feedback: string; count: number }>>>(
        STORAGE_KEYS.QUEST_FEEDBACK,
        {}
      ) ?? {};

    const questFeedbacks = feedbackMap[questTitle] || [];

    // Check if this feedback already exists
    const existingIndex = questFeedbacks.findIndex(
      (f) => f.feedback.toLowerCase() === feedback.toLowerCase()
    );

    if (existingIndex >= 0) {
      // Increment count
      questFeedbacks[existingIndex].count++;
    } else {
      // Add new feedback
      questFeedbacks.push({ feedback, count: 1 });
    }

    feedbackMap[questTitle] = questFeedbacks;
    storage.set(STORAGE_KEYS.QUEST_FEEDBACK, feedbackMap);
  },

  // Get top 5 most used feedbacks for a quest
  getTopFeedbacks: (questTitle: string, limit: number = 5): string[] => {
    const feedbackMap =
      storage.get<Record<string, Array<{ feedback: string; count: number }>>>(
        STORAGE_KEYS.QUEST_FEEDBACK,
        {}
      ) ?? {};

    const questFeedbacks = feedbackMap[questTitle] || [];

    // Sort by count (descending) and return top N
    return questFeedbacks
      .sort((a, b) => b.count - a.count)
      .slice(0, limit)
      .map((f) => f.feedback);
  },

  // Clear feedback history for a quest
  clearQuestFeedback: (questTitle: string): void => {
    const feedbackMap =
      storage.get<Record<string, Array<{ feedback: string; count: number }>>>(
        STORAGE_KEYS.QUEST_FEEDBACK,
        {}
      ) ?? {};

    delete feedbackMap[questTitle];
    storage.set(STORAGE_KEYS.QUEST_FEEDBACK, feedbackMap);
  },

  // Seed mock feedback data (for testing)
  seedMockFeedback: (): void => {
    const mockFeedbackData = {
      "Download BW and Earn": [
        {
          feedback:
            "Please verify that the email matches your Zealy account email.",
          count: 15,
        },
        {
          feedback: "Great job! Your submission has been approved.",
          count: 12,
        },
        {
          feedback:
            "The email format appears to be incorrect. Please double-check.",
          count: 8,
        },
        { feedback: "Thank you for completing this quest!", count: 6 },
        {
          feedback: "We need you to provide a valid email address to proceed.",
          count: 4,
        },
      ],
      "Follow on Twitter": [
        {
          feedback:
            "Please make sure you are following our official Twitter account.",
          count: 10,
        },
        { feedback: "Verified! Thank you for following us.", count: 9 },
        {
          feedback:
            "We could not verify your follow. Please check and resubmit.",
          count: 7,
        },
        { feedback: "Great! Welcome to our Twitter community.", count: 5 },
        {
          feedback: "Your Twitter handle does not match our records.",
          count: 3,
        },
      ],
      "Join Discord Server": [
        { feedback: "Welcome to our Discord community!", count: 11 },
        {
          feedback:
            "Please join the Discord server and verify your membership.",
          count: 9,
        },
        {
          feedback:
            "We could not find you in our Discord server. Please join first.",
          count: 6,
        },
        { feedback: "Discord username verified successfully!", count: 5 },
        {
          feedback:
            "Make sure to complete the verification process in Discord.",
          count: 4,
        },
      ],
    };

    storage.set(STORAGE_KEYS.QUEST_FEEDBACK, mockFeedbackData);
    console.log("✅ Mock feedback data seeded successfully");
  },
};

// User favorites tracking
export const favoritesStorage = {
  // Get all favorited community IDs for a user
  getUserFavorites: (userId: string): string[] => {
    const favoritesMap =
      storage.get<Record<string, string[]>>(STORAGE_KEYS.USER_FAVORITES, {}) ??
      {};
    return favoritesMap[userId] || [];
  },

  // Add a community to user's favorites
  addFavorite: (userId: string, communityId: string): void => {
    const favoritesMap =
      storage.get<Record<string, string[]>>(STORAGE_KEYS.USER_FAVORITES, {}) ??
      {};

    const userFavorites = favoritesMap[userId] || [];

    // Only add if not already favorited
    if (!userFavorites.includes(communityId)) {
      userFavorites.push(communityId);
      favoritesMap[userId] = userFavorites;
      storage.set(STORAGE_KEYS.USER_FAVORITES, favoritesMap);
      console.log(
        `❤️ Project ${communityId} added to favorites for user ${userId}`
      );
    }
  },

  // Remove a community from user's favorites
  removeFavorite: (userId: string, communityId: string): void => {
    const favoritesMap =
      storage.get<Record<string, string[]>>(STORAGE_KEYS.USER_FAVORITES, {}) ??
      {};

    const userFavorites = favoritesMap[userId] || [];
    favoritesMap[userId] = userFavorites.filter((id) => id !== communityId);
    storage.set(STORAGE_KEYS.USER_FAVORITES, favoritesMap);
    console.log(
      `💔 Project ${communityId} removed from favorites for user ${userId}`
    );
  },

  // Toggle favorite status
  toggleFavorite: (userId: string, communityId: string): boolean => {
    const favorites = favoritesStorage.getUserFavorites(userId);
    const isFavorited = favorites.includes(communityId);

    if (isFavorited) {
      favoritesStorage.removeFavorite(userId, communityId);
      return false;
    } else {
      favoritesStorage.addFavorite(userId, communityId);
      return true;
    }
  },

  // Check if a community is favorited by user
  isFavorited: (userId: string, communityId: string): boolean => {
    const favorites = favoritesStorage.getUserFavorites(userId);
    return favorites.includes(communityId);
  },

  // Get count of favorited projects
  getFavoritesCount: (userId: string): number => {
    return favoritesStorage.getUserFavorites(userId).length;
  },
};
